'use client';

/**
 * LUCAT CONNECT — Social News Store
 * Gestion des actualités sociales avec états (unread, seen, handled, deferred)
 * Chaque actualité référence un contactId et un contentId.
 * Les photos/noms sont toujours résolus via contactsById[contactId].
 */

import { CONTACTS, type Contact } from '@/lib/contacts';

// ── Contact lookup map ────────────────────────────────────────────────────
export const contactsById: Record<string, Contact> = Object.fromEntries(
  CONTACTS.map(c => [c.id, c])
);

export type NewsStatus = 'unread' | 'seen' | 'handled' | 'deferred';
export type NewsType = 'missed-call' | 'callback-request' | 'voice-message' | 'text-message' | 'photo' | 'event';

export interface SocialNewsItem {
  id: string;
  type: NewsType;
  status: NewsStatus;
  /** ID matching a key in contactsById */
  contactId: string;
  /** ID of the specific content (voice message, photo, text, etc.) */
  contentId: string;
  occurredAt: string;
  /** Human-readable date string */
  date: string;
  dateShort: string;
  /** Semantic icon color */
  iconColor: string;
  deferredUntil?: number;
}

// Priority order: missed-call=1, callback-request=2, voice-message=3, text-message=4, photo=5, event=6
const TYPE_PRIORITY: Record<NewsType, number> = {
  'missed-call': 1,
  'callback-request': 2,
  'voice-message': 3,
  'text-message': 4,
  'photo': 5,
  'event': 6,
};

// ── Derived display helpers ───────────────────────────────────────────────

export function getNotificationText(item: SocialNewsItem): string {
  const contact = contactsById[item.contactId];
  const name = contact?.firstName ?? item.contactId;
  switch (item.type) {
    case 'missed-call': return `${name} vous a appelé — ${item.dateShort}`;
    case 'callback-request': return `${name} demande à être rappelé`;
    case 'voice-message': return `${name} vous a laissé un message`;
    case 'text-message': return `${name} vous a envoyé un message`;
    case 'photo': return `${name} vous a envoyé une photo`;
    case 'event': return `Visite de ${name} prévue`;
    default: return `Nouveau message de ${name}`;
  }
}

export function getNotificationSubText(item: SocialNewsItem): string {
  switch (item.type) {
    case 'missed-call': return 'Appel manqué';
    case 'callback-request': return 'Demande de rappel';
    case 'voice-message': return 'Message vocal';
    case 'text-message': return 'Message';
    case 'photo': return 'Photo';
    case 'event': return 'Événement';
    default: return '';
  }
}

export function getNotificationPrimaryLabel(item: SocialNewsItem): string {
  const contact = contactsById[item.contactId];
  const name = contact?.firstName ?? item.contactId;
  switch (item.type) {
    case 'missed-call': return `Rappeler ${name}`;
    case 'callback-request': return `Appeler ${name}`;
    case 'voice-message': return 'Écouter le message';
    case 'text-message': return 'Lire le message';
    case 'photo': return 'Voir la photo';
    case 'event': return "Voir l'agenda";
    default: return 'Voir';
  }
}

/**
 * Builds the navigation href for a notification.
 * This is the single source of truth for routing.
 */
export function getNotificationHref(item: SocialNewsItem): string {
  switch (item.type) {
    case 'voice-message':
      return `/messages-and-photos-screen?contact=${item.contactId}&item=${item.contentId}&type=voice`;
    case 'text-message':
      return `/messages-and-photos-screen?contact=${item.contactId}&item=${item.contentId}&type=text`;
    case 'photo':
      return `/messages-and-photos-screen?contact=${item.contactId}&item=${item.contentId}&type=photo`;
    case 'missed-call': case'callback-request':
      return `/call-confirmation-screen?contact=${item.contactId}&notification=${item.id}`;
    case 'event':
      return `/ma-journee?item=${item.contentId}`;
    default:
      return `/messages-and-photos-screen?contact=${item.contactId}`;
  }
}

// ── Demo data ──────────────────────────────────────────────────────────────
const INITIAL_NEWS: SocialNewsItem[] = [
  {
    id: 'missed-call-001',
    type: 'missed-call',
    status: 'unread',
    contactId: 'marie',
    contentId: 'missed-call-001',
    occurredAt: '2026-07-27T16:20:00',
    date: "Aujourd'hui à 16h20",
    dateShort: '16h20',
    iconColor: '#D97706',
  },
  {
    id: 'voice-jean-001',
    type: 'voice-message',
    status: 'unread',
    contactId: 'jean',
    contentId: 'voice-jean-001',
    occurredAt: '2026-07-26T18:20:00',
    date: 'Hier à 18h20',
    dateShort: 'Hier 18h20',
    iconColor: '#2563EB',
  },
  {
    id: 'photo-marie-001',
    type: 'photo',
    status: 'unread',
    contactId: 'marie',
    contentId: 'photo-marie-001',
    occurredAt: '2026-07-27T14:32:00',
    date: "Aujourd'hui à 14h32",
    dateShort: '14h32',
    iconColor: '#16A34A',
  },
  {
    id: 'text-sophie-001',
    type: 'text-message',
    status: 'unread',
    contactId: 'sophie',
    contentId: 'text-sophie-001',
    occurredAt: '2026-07-27T11:05:00',
    date: "Aujourd'hui à 11h05",
    dateShort: '11h05',
    iconColor: '#2563EB',
  },
  {
    id: 'event-marie-001',
    type: 'event',
    status: 'unread',
    contactId: 'marie',
    contentId: 'event-marie-001',
    occurredAt: '2026-07-28T15:00:00',
    date: 'Demain à 15h00',
    dateShort: 'Demain 15h',
    iconColor: '#7C3AED',
  },
];

// ── In-memory store ────────────────────────────────────────────────────────
let _news: SocialNewsItem[] = INITIAL_NEWS.map(n => ({ ...n }));

export function getAllNews(): SocialNewsItem[] {
  return [..._news];
}

/** Active = unread or seen (not handled, not deferred or deferred expired) */
export function getActiveNews(): SocialNewsItem[] {
  const now = Date.now();
  return _news
    .filter(n => {
      if (n.status === 'handled') return false;
      if (n.status === 'deferred') {
        if (n.deferredUntil && now >= n.deferredUntil) {
          n.status = 'unread';
          return true;
        }
        return false;
      }
      return true;
    })
    .sort((a, b) => TYPE_PRIORITY[a.type] - TYPE_PRIORITY[b.type]);
}

export function getPriorityNews(): SocialNewsItem | null {
  const active = getActiveNews();
  return active[0] ?? null;
}

export function updateNewsStatus(id: string, status: NewsStatus): void {
  const item = _news.find(n => n.id === id);
  if (!item) return;
  item.status = status;
  if (status === 'deferred') {
    item.deferredUntil = Date.now() + 5 * 60 * 1000;
  }
}

export function markHandled(id: string): void {
  updateNewsStatus(id, 'handled');
}

export function markDeferred(id: string): void {
  updateNewsStatus(id, 'deferred');
}

export function resetAllNews(): void {
  _news = INITIAL_NEWS.map(n => ({ ...n }));
}

export function getActiveCount(): number {
  return getActiveNews().length;
}
